import { isNull } from 'lodash';
import AbstractService from './abstractService';

// Singleton instance
let instance = null;

const _SERVICE_UNAVAILABLE_ = new Error({ message : 'Service Unavailable' });

export default class InvoiceService extends AbstractService {
	static setup(baseURL, options) {
		instance = new InvoiceService(baseURL, options);
	}

	static getInstance() {
		return instance;
	}

	constructor(baseURL, options) {
		super(baseURL, options);
		this.endpoint = 'invoices';
	}

	static changeInvoiceUserStatus(...params) {
		if (isNull(instance)) return Promise.reject(_SERVICE_UNAVAILABLE_);

		return instance._changeInvoiceUserStatus(...params);
	}

	static getInvoiceList(...params) {
		if (isNull(instance)) return Promise.reject(_SERVICE_UNAVAILABLE_);

		return instance._getInvoiceList(...params);
	}

	static getInvoiceData(...params) {
		if (isNull(instance)) return Promise.reject(_SERVICE_UNAVAILABLE_);

		return instance._getInvoiceData(...params);
	}

	static getCounts(...params) {
		if (isNull(instance)) return Promise.reject(_SERVICE_UNAVAILABLE_);

		return instance._getCounts(...params);
	}

	static getDisputeReasons() {
		if (isNull(instance)) return Promise.reject(_SERVICE_UNAVAILABLE_);

		return instance._getDisputeReasons();
	}

	static submitDisputeForm(...params) {
		if (isNull(instance)) return Promise.reject(_SERVICE_UNAVAILABLE_);

		return instance._submitDisputeForm(...params);
	}

	static getInvoiceSummary(...params) {
		if (isNull(instance)) return Promise.reject(_SERVICE_UNAVAILABLE_);

		return instance._getInvoiceSummary(...params);
	}

	static getReceiptDetails(...params) {
		if (isNull(instance)) return Promise.reject(_SERVICE_UNAVAILABLE_);

		return instance._getReceiptDetails(...params);
	}

	static exportCSV(...params) {
		if (isNull(instance)) return Promise.reject(_SERVICE_UNAVAILABLE_);

		return instance._exportCSV(...params);
	}

	static downloadReceiptPDF(...params) {
		if (isNull(instance)) return Promise.reject(_SERVICE_UNAVAILABLE_);

		return instance._downloadReceiptPDF(...params);
	}

	static downloadInvoicePDF(...params) {
		if (isNull(instance)) return Promise.reject(_SERVICE_UNAVAILABLE_);

		return instance._downloadInvoicePDF(...params);
	}

	static getInvoiceHistoryReportData(...params) {
		if (isNull(instance)) return Promise.reject(_SERVICE_UNAVAILABLE_);

		return instance._getInvoiceHistoryReportData(...params);
	}

	static getPaymentHistoryReportData(...params) {
		if (isNull(instance)) return Promise.reject(_SERVICE_UNAVAILABLE_);

		return instance._getPaymentHistoryReportData(...params);
	}

	static getTransactionHistoryReportData(...params) {
		if (isNull(instance)) return Promise.reject(_SERVICE_UNAVAILABLE_);

		return instance._getTransactionHistoryReportData(...params);
	}

	static getInvoiceHistoryExportData(...params) {
		if (isNull(instance)) return Promise.reject(_SERVICE_UNAVAILABLE_);

		return instance._getInvoiceHistoryExportData(...params);
	}

	static getPaymentHistoryExportData(...params) {
		if (isNull(instance)) return Promise.reject(_SERVICE_UNAVAILABLE_);

		return instance._getPaymentHistoryExportData(...params);
	}

	static getTransactionHistoryExportData(...params) {
		if (isNull(instance)) return Promise.reject(_SERVICE_UNAVAILABLE_);

		return instance._getTransactionHistoryExportData(...params);
	}

	static getBulkQuickPayInvoice(...params) {
		if (isNull(instance)) return Promise.reject(_SERVICE_UNAVAILABLE_);

		return instance._getBulkQuickPayInvoice(...params);
	}

	static getQuickPayInvoice(...params) {
		if (isNull(instance)) return Promise.reject(_SERVICE_UNAVAILABLE_);

		return instance._getQuickPayInvoice(...params);
	}

	_changeInvoiceUserStatus({ idArray, status }) {
		return this.put({
			endpoint : `v2/${this.endpoint}/status`,
			data     : {
				InvoiceIds    : idArray,
				InvoiceStatus : status,
			},
		});
	}

	_getInvoiceList({
		getCancelToken,
		...queryParams
	}) {
		return this.get({
			endpoint : `${this.endpoint}`,
			queryParams,
			getCancelToken,
		});
	}

	_getInvoiceData(id) {
		return this.get({
			endpoint    : `${this.endpoint}/`,
			queryParams : { id },
		});
	}

	_getCounts({ queryParams }) {
		return this.get({
			endpoint : `${this.endpoint}/counts/`,
			queryParams,
		});
	}

	_getDisputeReasons() {
		return this.get({
			endpoint : `${this.endpoint}/disputeReasons/`,
		});
	}

	_submitDisputeForm(Id, Note, DisputeReasonId) {
		return this.post({
			endpoint : `${this.endpoint}/${Id}/dispute/`,
			data     : {
				Note,
				DisputeReasonId,
			},
		});
	}

	_getInvoiceSummary({ queryParams, data }) {
		return this.post({
			endpoint : `${this.endpoint}/transactions/`,
			queryParams,
			data,
		});
	}

	_getReceiptDetails(transactionId) {
		return this.get({
			endpoint    : `${this.endpoint}/transaction/receipt`,
			queryParams : { transactionId },
		});
	}

	_exportCSV(queryParams) {
		return this.get({
			endpoint : `${this.endpoint}/csv`,
			queryParams,
		});
	}

	_downloadReceiptPDF({ id }) {
		return this.request({
			endpoint     : `${this.endpoint}/transaction/receipt/${id}/pdf`,
			responseType : 'blob',
		});
	}

	_downloadInvoicePDF({ id }) {
		return this.request({
			endpoint     : `${this.endpoint}/${id}/downloadpdf`,
			responseType : 'blob',
		});
	}

	_getInvoiceHistoryReportData(queryParams) {
		return this.get({
			endpoint : `${this.endpoint}/report`,
			queryParams,
		});
	}

	_getPaymentHistoryReportData(queryParams) {
		return this.get({
			endpoint : `${this.endpoint}/payments/report`,
			queryParams,
		});
	}

	_getTransactionHistoryReportData(queryParams) {
		return this.get({
			endpoint : 'receipts',
			queryParams,
		});
	}

	_getInvoiceHistoryExportData(queryParams, exportType) {
		return this.request({
			endpoint     : `${this.endpoint}/report/history/${exportType}`,
			responseType : 'blob',
			queryParams,
		});
	}

	_getPaymentHistoryExportData(queryParams, exportType) {
		return this.request({
			endpoint     : `${this.endpoint}/payments/report/${exportType}`,
			responseType : 'blob',
			queryParams,
		});
	}

	_getTransactionHistoryExportData(queryParams, exportType) {
		return this.request({
			endpoint     : `receipts/report/${exportType}`,
			responseType : 'blob',
			queryParams,
		});
	}

	_getBulkQuickPayInvoice(hash) {
		return this.get({
			endpoint : `${this.endpoint}/tokenized/${hash}/list`,
		});
	}

	_getQuickPayInvoice(hash) {
		return this.get({
			endpoint : `${this.endpoint}/tokenized/${hash}`,
		});
	}
}
